import { useState } from "react";
import { useData } from "@/context/data-provider";
import type { TodoPriority } from "@/types";
import { todayKey } from "@/lib/constants";

type Filter = "all" | "alta" | "normal" | "baixa" | "vence";

const prioColor: Record<TodoPriority, string> = { alta: "var(--mv-red)", normal: "var(--mv-ink-xlight)", baixa: "var(--mv-blue)" };

function dueStyle(due: string | null) {
  if (!due) return { color: "var(--mv-ink-xlight)" };
  const today = todayKey();
  if (due < today) return { color: "var(--mv-red)", fontWeight: 600 };
  const in3 = new Date(); in3.setDate(in3.getDate() + 3);
  if (due <= in3.toISOString().slice(0, 10)) return { color: "var(--mv-orange)" };
  return { color: "var(--mv-ink-xlight)" };
}

export default function TodoPage() {
  const { todos: { todos, addTodo, toggleTodo, deleteTodo, clearAll } } = useData();
  const [text, setText] = useState("");
  const [prio, setPrio] = useState<TodoPriority>("normal");
  const [due, setDue] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const handleAdd = () => {
    if (!text.trim()) return;
    addTodo({ text: text.trim(), prio, due: due || null });
    setText(""); setPrio("normal"); setDue("");
  };

  const sorted = [...todos].sort((a, b) => {
    const po = { alta: 0, normal: 1, baixa: 2 };
    if (po[a.prio] !== po[b.prio]) return po[a.prio] - po[b.prio];
    if (a.due && b.due) return a.due.localeCompare(b.due);
    if (a.due) return -1; if (b.due) return 1;
    return b.createdAt.localeCompare(a.createdAt);
  });

  const today = todayKey();
  const in7 = new Date(); in7.setDate(in7.getDate() + 7);
  const in7str = in7.toISOString().slice(0, 10);

  const filtered = sorted.filter((t) => {
    switch (filter) {
      case "alta": case "normal": case "baixa": return t.prio === filter;
      case "vence": return t.due != null && t.due <= in7str && !t.done;
      default: return true;
    }
  });

  const pendingCount = todos.filter((t) => !t.done).length;

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-serif text-[1.3rem]" style={{ color: "var(--mv-ink-dark)" }}>To Do</h1>
          <span className="font-mono text-[0.68rem]" style={{ color: "var(--mv-ink-light)" }}>{pendingCount} pendente{pendingCount !== 1 ? "s" : ""}</span>
        </div>
        {todos.length > 0 && (
          <button className="mv-btn mv-btn-danger mv-btn-sm" onClick={clearAll}>Limpar tudo</button>
        )}
      </div>

      {/* Input */}
      <div className="mv-panel mb-4">
        <div className="p-[14px_18px]">
          <div className="flex flex-col gap-[9px]">
            <input
              className="mv-input"
              placeholder="Nova tarefa..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 9 }}>
              <div>
                <label className="mv-label">Prioridade</label>
                <select className="mv-select" value={prio} onChange={(e) => setPrio(e.target.value as TodoPriority)}>
                  <option value="alta">Alta 🔴</option>
                  <option value="normal">Normal</option>
                  <option value="baixa">Baixa 🔵</option>
                </select>
              </div>
              <div>
                <label className="mv-label">Data limite</label>
                <input type="date" className="mv-input" value={due} onChange={(e) => setDue(e.target.value)} />
              </div>
            </div>
            <button className="mv-btn mv-btn-primary" onClick={handleAdd}>+ Adicionar</button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-[7px] mb-3 flex-wrap">
        {([
          ["all", "Todas"], ["alta", "Alta 🔴"], ["normal", "Normal"], ["baixa", "Baixa 🔵"], ["vence", "⏰ A Vencer"],
        ] as [Filter, string][]).map(([f, label]) => (
          <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>{label}</button>
        ))}
      </div>

      {/* List */}
      <div className="flex flex-col gap-2">
        {filtered.length === 0 ? (
          <div className="mv-panel"><div className="empty-state"><div className="empty-icon">✅</div>Nenhuma tarefa</div></div>
        ) : (
          filtered.map((t) => (
            <div key={t.id} className="todo-item" style={{ opacity: t.done ? 0.5 : 1 }}>
              <div className="todo-priority" style={{ background: prioColor[t.prio] }} />
              <button
                className={`todo-check ${t.done ? "checked" : ""}`}
                onClick={() => toggleTodo(t.id)}
              >
                ✓
              </button>
              <div className="flex-1">
                <div className="text-[0.84rem] leading-[1.4]" style={{ color: "var(--mv-ink-dark)", textDecoration: t.done ? "line-through" : undefined }}>{t.text}</div>
                <div className="font-mono text-[0.58rem] mt-[3px]" style={{ color: "var(--mv-ink-xlight)" }}>
                  {t.due && <span style={dueStyle(t.due)}>Vence: {t.due}</span>}
                  {t.due && " · "}
                  {new Date(t.createdAt).toLocaleDateString("pt-PT")}
                </div>
              </div>
              <button
                className="text-base opacity-0 hover:opacity-100 transition-opacity p-1"
                style={{ color: "var(--mv-red)" }}
                onClick={() => deleteTodo(t.id)}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
